import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  CircularProgress,
  AppBar,
  Toolbar,
  IconButton,
  Chip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import LogoutIcon from '@mui/icons-material/Logout';
import PlaylistPlayIcon from '@mui/icons-material/PlaylistPlay';
import { playlistService } from '../services/api.ts';
import { useAuthenticatedAxios } from '../services/useAuthenticatedAxios';
import { signOutRedirect } from '../utils/authUtils.ts';

interface PlaylistData {
  playlistId: string;
  playlistName: string;
  playlistDescription: string;
  playlistPicUrl: string | null;
}

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'all 0.3s ease-in-out',
  cursor: 'pointer',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: theme.shadows[8],
  },
}));

const CardMediaWrapper = styled(Box)(({ theme }) => ({
  height: 200,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: theme.palette.grey[100],
  color: theme.palette.grey[500],
  '& svg': {
    fontSize: '4rem',
  },
}));

const PlaylistPage = () => {
  const [playlists, setPlaylists] = useState<PlaylistData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const auth = useAuth();
  const navigate = useNavigate();
  const api = useAuthenticatedAxios();

  useEffect(() => {
    fetchPlaylists();
  }, []);

  const fetchPlaylists = async () => {
    try {
      const response = await playlistService.listPlaylists(api);
      if (response.success) {
        setPlaylists(response.data);
        setError(null);
      } else {
        setError(response.message || 'Failed to fetch playlists');
      }
    } catch (err) {
      setError('Failed to fetch playlists. Please try again later.');
      console.error('Error fetching playlists:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePlaylistClick = (playlistId: string) => {
    navigate(`/media?playlist=${playlistId}`);
  };

  const handleLogout = () => {
    signOutRedirect(auth);
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar position="static">
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            The French Journey - Playlists
          </Typography>
          <IconButton color="inherit" onClick={handleLogout}>
            <LogoutIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        {error ? (
          <Typography color="error" align="center" gutterBottom>
            {error}
          </Typography>
        ) : (
          <>
            <Box sx={{ mb: 4, textAlign: 'center' }}>
              <Typography variant="h3" gutterBottom sx={{ fontWeight: 'bold' }}>
                French Learning Playlists
              </Typography>
              <Typography variant="h6" color="text.secondary">
                Explore curated collections of French learning content
              </Typography>
            </Box>

            {playlists.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <PlaylistPlayIcon sx={{ fontSize: '4rem', color: 'grey.400', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  No playlists available yet
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Check back later for new learning content
                </Typography>
              </Box>
            ) : (
              <Grid container spacing={3}>
                {playlists.map((playlist) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={playlist.playlistId}>
                    <StyledCard onClick={() => handlePlaylistClick(playlist.playlistId)}>
                      {playlist.playlistPicUrl ? (
                        <CardMedia
                          component="img"
                          height="200"
                          image={playlist.playlistPicUrl}
                          alt={playlist.playlistName}
                        />
                      ) : (
                        <CardMediaWrapper>
                          <PlaylistPlayIcon />
                        </CardMediaWrapper>
                      )}
                      <CardContent sx={{ flexGrow: 1, p: 3 }}>
                        <Typography gutterBottom variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
                          {playlist.playlistName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          {playlist.playlistDescription}
                        </Typography>
                        <Chip 
                          label="View Playlist" 
                          color="primary" 
                          size="small"
                          sx={{ cursor: 'pointer' }}
                        />
                      </CardContent>
                    </StyledCard>
                  </Grid>
                ))}
              </Grid>
            )}
          </>
        )}
      </Container>
    </Box>
  );
};

export default PlaylistPage; 